import { FC, ReactNode } from 'react'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import styles from './reviewStatsCards.module.scss'

interface StatCardProps {
  change?: number | null
  subtitle?: string
  title: string
  trendLabel?: string
  value: string | ReactNode
  // Small text after the value, e.g. "of all reviews are positive."
  valueCaption?: string
}

// One metric card. Also used on its own, e.g. the platform page's Positive
// Review Rate.
export const StatCard: FC<StatCardProps> = ({
  change,
  subtitle,
  title,
  trendLabel,
  value,
  valueCaption,
}) => (
  <div className={styles.metricCard}>
    <div className={styles.cardHeader}>
      <span className={styles.cardTitle}>{title}</span>
      {subtitle && <span className={styles.cardSubtitle}>{subtitle}</span>}
    </div>
    <div className={styles.cardBody}>
      <div className={styles.valueGroup}>
        <div className={styles.mainValue}>{value}</div>
        {valueCaption && (
          <span className={styles.valueCaption}>{valueCaption}</span>
        )}
      </div>
      {change != null && (
        <div className={styles.trendSection}>
          <div
            className={`${styles.trendBadge} ${change < 0 ? styles.negative : ''}`}
          >
            <TrendUpIcon
              className={styles.trendIcon}
              style={change < 0 ? { transform: 'rotate(180deg)' } : undefined}
            />
            <span className={styles.trendValue}>{Math.abs(change)}%</span>
          </div>
          <span className={styles.trendLabel}>
            {trendLabel ?? 'Last 30 days'}
          </span>
        </div>
      )}
    </div>
  </div>
)

// The change badge without its caption — the combined card states the window
// once, as the Average Rating subtitle.
const TrendBadge: FC<{ change: number }> = ({ change }) => (
  <div className={`${styles.trendBadge} ${change < 0 ? styles.negative : ''}`}>
    <TrendUpIcon
      className={styles.trendIcon}
      style={change < 0 ? { transform: 'rotate(180deg)' } : undefined}
    />
    <span className={styles.trendValue}>{Math.abs(change)}%</span>
  </div>
)

interface CombinedHalfProps {
  change?: number
  subtitle?: string
  title: string
  value: string | ReactNode
}

const CombinedHalf: FC<CombinedHalfProps> = ({
  change,
  subtitle,
  title,
  value,
}) => (
  <div className={styles.combinedHalf}>
    <div className={styles.cardHeader}>
      <span className={styles.combinedTitle}>{title}</span>
      {subtitle && <span className={styles.cardSubtitle}>{subtitle}</span>}
    </div>
    <div className={styles.combinedValueRow}>
      <div className={styles.mainValue}>{value}</div>
      {change != null && <TrendBadge change={change} />}
    </div>
  </div>
)

interface ReviewStatsCardsProps {
  averageRating?: number
  averageRatingChange?: number
  // One card with Total Reviews and Average Rating side by side (the
  // redesigned Executive Overview). Off: two separate cards, as the facility
  // and platform pages still lay them out.
  combined?: boolean
  // The location the numbers cover when one is selected; default "All
  // Locations".
  locationLabel?: string
  // Combined card only: the window the numbers cover, shown under Average
  // Rating (e.g. "Last 30 days").
  periodLabel?: string
  totalReviews?: number
  totalReviewsChange?: number
  // Combined card only: the left half's title. A custom title drops the
  // "All Locations" subtitle (e.g. "Reviews" on a facility card).
  totalTitle?: string
  // Caption under the change badge. Defaults to the unfiltered behaviour
  // ("Last 30 days" rolling window); pass e.g. "vs previous period" when a
  // date filter drives the deltas so the label doesn't lie.
  trendLabel?: string
}

export const ReviewStatsCards: FC<ReviewStatsCardsProps> = ({
  averageRating,
  averageRatingChange,
  combined,
  locationLabel = 'All Locations',
  periodLabel,
  totalReviews,
  totalReviewsChange,
  totalTitle,
  trendLabel,
}) => {
  const ratingValue = (
    <div className={styles.ratingValueWrapper}>
      <StarIcon className={styles.ratingStarIcon} />
      {averageRating?.toFixed(1) ?? '—'}
    </div>
  )

  if (combined) {
    return (
      <div className={`${styles.metricCard} ${styles.combinedCard}`}>
        <CombinedHalf
          change={totalReviewsChange}
          subtitle={totalTitle ? undefined : locationLabel}
          title={totalTitle ?? 'Total Reviews'}
          value={totalReviews?.toLocaleString() ?? '—'}
        />
        <div className={styles.combinedDivider} />
        <CombinedHalf
          change={averageRatingChange}
          subtitle={periodLabel ?? trendLabel ?? 'Last 30 days'}
          title="Average Rating"
          value={ratingValue}
        />
      </div>
    )
  }

  return (
    <>
      <StatCard
        change={totalReviewsChange}
        subtitle={locationLabel}
        title="Total Reviews"
        trendLabel={trendLabel}
        value={totalReviews?.toLocaleString() ?? '—'}
      />
      <StatCard
        change={averageRatingChange}
        title="Average Rating"
        trendLabel={trendLabel}
        value={ratingValue}
      />
    </>
  )
}
